import { ApiError } from "@aidkitorg/aidkit/lib/common/api.types";
import { SummaryExpr } from "@aidkitorg/types/lib/survey";
import * as Sentry from "@sentry/react";
import { Row } from "@tanstack/react-table";
import { convertFilterStringToValuesToMatch, highlightText, Icon } from "@aidkitorg/component-library";
import { AdminPanelCollection } from "@aidkitorg/types/lib/admin-panel/collections";
import { useLocation } from "react-router-dom";

// TODO: i18n
export const generateTimestampString =  (timestamp: number) => {
  const date = new Date(timestamp);

  return (
    <>
      {date.toLocaleDateString()} {date.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "numeric",
      })}
    </>
  )
}

/**
 * Given a timestamp returned by something like Date.now(), returns that time
 * formatted in MM/DD/YYYY format.
 * 
 * @param timestamp timestamp in ms elapsed since epoch
 */
export const generateDateString = (timestamp: number) => {
  const date = new Date(timestamp);

  const month = date.getMonth() + 1;
  const day = date.getDate();
  const year = date.getFullYear();

  const formattedDate = `${month}/${day}/${year}`;

  return formattedDate
}

/**
 * Given a candidate string, validates whether it is a valid email format. 
 * Used in `InputWithTags` component.
 * 
 * @param candidate candidate to validate
 * @returns boolean 
 */
export const onlyEmails = (candidate: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(candidate)
};

/**
 * Given an error, logs the error as an admin panel error in Sentry
 * 
 * @param error error to log
 */
export const logAdminPanelError = (error: ApiError, message: string) => {
  Sentry.withScope((scope) => {
    scope.setTag("surface_area", "admin_panel")
    scope.setTag("error_code", error.code)
    scope.setTag("error_source", error.source)
    
    Sentry.captureException(new Error('Admin Panel Error ' + error.code + ": " + message))
  })
}  

/**
 * Given an aggregation type and a data field, genearates a distro Summary kind
 * 
 * @param aggregation aggregation type for summary
 * @param dataField target field to aggregate on
 */
export const generateDistroSummaryKind = (aggregation: string, dataField: string): SummaryExpr => {
  switch (aggregation) {
    case 'Count': 
      return { kind: 'Count' }
    case 'Sum':
      return {
        kind: 'Sum',
        value: {
          kind: 'Field',
          field: dataField
        }
      }
    case 'Mean':
      return {
        kind: 'Mean',
        value: {
          kind: 'Field', 
          field: dataField
        }
      }
  }

  return { kind: 'Count' }
}

/**
 * Converts a string into snake_case format.
 *
 * This function:
 * - Converts camelCase to snake_case
 * - Replaces spaces and dashes with underscores
 * - Removes non-alphanumeric characters (excluding underscores)
 * - Lowercases the entire result
 *
 * @param input - The input string to convert (e.g. a dashboard title)
 * @returns The snake_case version of the input string
 *
 * @example
 * toSnakeCase("Dashboard Title") // "dashboard_title"
 * toSnakeCase("RevenueByRegion2025") // "revenue_by_region2025"
 * toSnakeCase("My-Dashboard!") // "my_dashboard"
 */
export const toSnakeCase = (input: string): string => {
  return input
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')      // camelCase → camel_Case
    .replace(/[\s\-]+/g, '_')                 // spaces and dashes → underscores
    .replace(/[^a-zA-Z0-9_]/g, '')            // remove other special characters
    .toLowerCase();
}

/**
 * Returns today's date formatted as a compact YYYYMMDD string.
 *
 * This is useful for filenames, timestamps, and compact date tracking.
 * Pads month and day with leading zeros to ensure 2-digit format.
 *
 * @returns A string representing today’s date in YYYYMMDD format (e.g., "20250724")
 *
 * @example
 * getTodayAsYYYYMMDD(); // "20250724" if today is July 24, 2025
 */
export const getTodayAsYYYYMMDD = (): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
  const dd = String(today.getDate()).padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
}

/**
 * Converts a string to PascalCase format.
 *
 * This function:
 * - Lowercases the input
 * - Splits on spaces, dashes, or underscores
 * - Capitalizes the first letter of each word
 * - Joins the words without separators
 *
 * @param input - The input string to convert (e.g., "my-dashboard name")
 * @returns The PascalCase version of the string (e.g., "MyDashboardName")
 *
 * @example
 * toPascalCase("dashboard name") // "DashboardName"
 * toPascalCase("my-dashboard")   // "MyDashboard"
 * toPascalCase("snake_case_name") // "SnakeCaseName"
 */
export const toPascalCase = (input: string): string => {
  return input
    .toLowerCase()
    .split(/[\s-_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
}

export type { AdminPanelCollection };

/** A synthetic folder row — only contains the fields needed for rendering a collection. */
export type FolderRow<T> = {
  id: string;
  title: string;
  isCollection: true;
  subRows?: CollectionRow<T>[];
  limitedToUserGroups?: AdminPanelCollection['limitedToUserGroups'];
  limitedToUserGroupsFromTags?: AdminPanelCollection['limitedToUserGroupsFromTags'];
  /** Per-kind item counts for this folder across the whole admin panel (see {@link AdminPanelCollection.itemCounts}). */
  itemCounts?: AdminPanelCollection['itemCounts'];
};

/** A real data row, guaranteed not to be a folder. */
export type ItemRow<T> = T & { isCollection?: never; subRows?: CollectionRow<T>[] };

/** Discriminated union of folder rows and real data rows. */
export type CollectionRow<T> = FolderRow<T> | ItemRow<T>;

function buildCollectionRow<T extends { id: string }>(
  items: T[],
  col: AdminPanelCollection,
  categorizedIds: Set<string>
): FolderRow<T> | null {
  const directItems = items.filter(item => col.itemIds.includes(item.id));
  directItems.forEach(item => categorizedIds.add(item.id));

  const subCollectionRows = col.subCollections
    .map(sub => buildCollectionRow(items, sub, categorizedIds))
    .filter((r): r is FolderRow<T> => r !== null);

  const allSubRows: CollectionRow<T>[] = [...subCollectionRows, ...directItems];
  if (allSubRows.length === 0) return null;

  const row: FolderRow<T> = { id: col.id, title: col.name, isCollection: true, subRows: allSubRows };
  if (col.limitedToUserGroups?.length) row.limitedToUserGroups = col.limitedToUserGroups;
  if (col.limitedToUserGroupsFromTags?.length) row.limitedToUserGroupsFromTags = col.limitedToUserGroupsFromTags;
  if (col.itemCounts) row.itemCounts = col.itemCounts;
  return row;
}

export function buildCollectionRows<T extends { id: string; title: string }>(
  items: T[],
  collections: AdminPanelCollection[]
): CollectionRow<T>[] {
  if (collections.length === 0) return [...items];

  const categorizedIds = new Set<string>();
  const collectionRows = collections
    .map(col => buildCollectionRow(items, col, categorizedIds))
    .filter((r): r is FolderRow<T> => r !== null);

  const uncategorized = items.filter(item => !categorizedIds.has(item.id));
  return [...collectionRows, ...uncategorized];
}

/** True if a collection with the given id exists anywhere in the tree. */
function collectionExists(collections: AdminPanelCollection[], id: string): boolean {
  return collections.some(col => col.id === id || collectionExists(col.subCollections, id));
}

/**
 * Reads `?folder=<collectionId>` from the URL and returns it as a `DataTable` `seekToRowId`.
 * Folder rows use the collection id as their row id (see `buildCollectionRow`), so the URL
 * param maps directly. Returns undefined when absent or when no such collection exists.
 */
export function useFolderSeek(collections: AdminPanelCollection[]): string | undefined {
  const { search } = useLocation();
  const folderId = new URLSearchParams(search).get('folder') ?? undefined;
  if (!folderId || !collectionExists(collections, folderId)) return undefined;
  return folderId;
}

/**
 * TanStack Table globalFilterFn that matches row cell values AND ancestor collection names.
 * Supports quoted phrases and multi-term AND matching (same as DEFAULT_GLOBAL_FILTER_FN).
 * Works for any row type — use as `globalFilterFn` alongside `filterFromLeafRows={true}`.
 */
export function collectionAwareFilterFn<T>(row: Row<T>, _columnId: string, filterValue: string): boolean {
  const terms = convertFilterStringToValuesToMatch(filterValue);
  const rowValues = row.getAllCells().flatMap(cell => {
    const val = cell.getValue();
    return val ? [JSON.stringify(val).toLowerCase()] : [];
  });

  const ancestorTitles: string[] = [];
  let parent = row.getParentRow();
  while (parent) {
    const title = ((parent.original as any).title ?? '').toLowerCase();
    if (title) ancestorTitles.push(title);
    parent = parent.getParentRow();
  }

  // Every term must match at least one row cell OR an ancestor collection title
  return terms.every((term: string) =>
    rowValues.some(v => v.includes(term)) || ancestorTitles.some(t => t.includes(term))
  );
}

/**
 * Global filter function for DataTable that treats underscores as spaces
 * and uses word-boundary matching for tokens containing digits
 * (e.g. "2" matches "2 flu" but not "22 flu").
 */
export function adminPanelGlobalFilterFn<T>(row: Row<T>, _columnId: string, filterValue: string): boolean {
  if (!filterValue) return true;
  const tokens = convertFilterStringToValuesToMatch(filterValue).map((t: string) => t.replace(/_/g, ' '));
  const rowValues = row.getAllCells().flatMap(cell => {
    const v = cell.getValue();
    return v ? [JSON.stringify(v).toLowerCase().replace(/_/g, ' ')] : [];
  });
  return tokens.every((token: string) => {
    if (/\d/.test(token)) {
      const re = new RegExp(`\\b${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      return rowValues.some(rv => re.test(rv));
    }
    return rowValues.some(rv => rv.includes(token));
  });
}

export function CollectionFolderCell<T extends { title: string }>({ row, search }: { row: Row<T>; search?: string }) {
  const isExpanded = row.getIsExpanded();
  return (
    <button
      className="flex items-center gap-2 w-full text-left py-0.5"
      style={{ paddingLeft: row.depth > 0 ? `${row.depth * 16}px` : undefined }}
      onClick={(e) => {
        e.stopPropagation();
        row.getToggleExpandedHandler()();
      }}
    >
      <Icon
        name={isExpanded ? 'FolderOpenIcon' : 'FolderIcon'}
        size="sm"
        role="decorative"
        className="text-indigo-500 flex-shrink-0"
      />
      <span className="text-gray-900">
        {search ? highlightText(row.original.title, search) : row.original.title}
      </span>
      <Icon
        name={isExpanded ? 'ChevronDownIcon' : 'ChevronRightIcon'}
        size="sm"
        role="decorative"
        className="text-gray-400 ml-1"
      />
    </button>
  );
}